import { ensureCSRFToken, fetchAPI } from './backendClient';

export interface User {
  id: number;
  email: string;
}

export type SubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'past_due'
  | 'incomplete'
  | 'trialing'
  | 'none';

export interface Subscription {
  status: SubscriptionStatus;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface SessionResponse {
  user: User | null;
  subscription: Subscription | null;
  requires_payment: boolean;
}

/**
 * Send magic link to user's email for passwordless login.
 * Creates user if they don't exist (passwordless signup).
 */
export async function sendMagicLink(email: string): Promise<void> {
  // Ensure CSRF token exists for anonymous users
  await ensureCSRFToken();

  const response = await fetchAPI('/v1/auth/login/', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || error.error || 'Failed to send magic link');
  }
}

/**
 * Verify magic link token and create session.
 * Returns user data on success.
 */
export async function verifyMagicLink(token: string): Promise<void> {
  const response = await fetchAPI('/v1/auth/verify/', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.detail || 'Invalid or expired magic link');
  }
}

/**
 * Get current session information.
 * Returns user data if authenticated, null if not.
 * Always returns 200 OK - no errors thrown for unauthenticated state.
 */
export async function getSession(): Promise<SessionResponse> {
  const response = await fetchAPI('/v1/auth/session/');

  if (!response.ok) {
    throw new Error('Failed to get session');
  }

  return response.json();
}

/**
 * Log out the current user.
 * Destroys the Django session.
 */
export async function logout(): Promise<void> {
  const response = await fetchAPI('/v1/auth/logout/', {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error('Failed to logout');
  }
}
